import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from './ChildrenContext.js';
import PropTypes from 'prop-types';
import DropdownList from 'react-widgets/DropdownList';
import CollapsibleMultiselect from './Multiselects/CollapsibleMultiselect';
import Loading from './Loading';
import Controller from '../controllers/DynamicSelectSegmentController';
import debounce from '../helpers/debounce';
import query from '../helpers/query';
import typeOf from '../helpers/typeOf';

const minLength = 3;

const normalizeMultiValues = (input) => {
    if (!Array.isArray(input)) {
        return [];
    }

    return input.map((item) => {
        if (typeOf.isObject(item) && item !== null) {
            if (item.value !== undefined && item.value !== null) {
                return item.value;
            }
            if (item.id !== undefined && item.id !== null) {
                return item.id;
            }
        }

        return item;
    }).filter((item) => item !== undefined && item !== null);
};

const normalizeSingleValue = (input) => {
    if (typeOf.isObject(input) && input !== null) {
        if (input.value !== undefined && input.value !== null) {
            return input.value;
        }
        if (input.id !== undefined && input.id !== null) {
            return input.id;
        }
    }

    return input ?? null;
};

const shallowEqualArray = (a = [], b = []) => {
    if (a.length !== b.length) {
        return false;
    }

    for (let i = 0; i < a.length; i++) {
        if (String(a[i]) !== String(b[i])) {
            return false;
        }
    }

    return true;
};

const DynamicSelectSegment = ({
    segment,
    values = [],
    count = 15,
    multiselect = false,
    extraParams = {},
    onAction,
    t
}) => {
    const elemRef = useRef(null);
    const onMoreRef = useRef(() => {});

    const messagesStatic = (t && t.multiselect) || {};
    const reportPageStatic = (t && t.reportPage) || {};

    const [state, setState] = useState({
        segment: segment,
        values: values,
        loading: false,
        page: 1,
        count: count,
        total: null,
        open: false,
        searchTerm: '',
        // the q of the request the current list was loaded with ('' = not filtered by the server)
        serverTerm: '',
        list: []
    });

    // Search: a term of minLength+ chars (any length for Country) is searched by the server (case-insensitive);
    // a shorter term filters the loaded list here (case-insensitive, see onFilter)
    const isServerTerm = useCallback((term) => {
        const trimmed = (term || '').trim();
        return trimmed.length >= minLength || (trimmed.length > 0 && 'Country' === state.segment);
    }, [state.segment]);

    const requestIdRef = useRef(0);

    const fetchList = useCallback((term, page = 1) => {
        if (!state.segment) {
            return;
        }

        const q = isServerTerm(term) ? (term || '').trim() : '';
        const requestId = ++requestIdRef.current;

        setState(prevState => ({
            ...prevState,
            loading: true
        }));

        Controller.searchDataSegment({
            segment: state.segment,
            params: {
                ...extraParams,
                count: state.count,
                page,
                q,
                types: state.segment.toLowerCase()
            }
        }, (data) => {
            if (requestId !== requestIdRef.current) {
                return;
            }
            setState(prevState => ({
                ...prevState,
                list: page > 1 ? prevState.list.concat(data.list) : data.list,
                page: data.page,
                total: data.total,
                serverTerm: q,
                loading: false
            }));
        });

        // if the number, then look for id
        if (q && typeOf.isNumeric(q) && page === 1) {
            Controller.searchDataSegment({
                segment: state.segment,
                params: {
                    ...extraParams,
                    ids: q
                }
            }, (data) => {
                if (requestId !== requestIdRef.current || !data.list.length) {
                    return;
                }
                setState(prevState => (
                    prevState.list.some((item) => String(item.value) === String(data.list[0].value))
                        ? prevState
                        : { ...prevState, list: [data.list[0], ...prevState.list] }
                ));
            });
        }
    }, [state.segment, state.count, extraParams, isServerTerm]);

    const fetchListRef = useRef(fetchList);
    useEffect(() => {
        fetchListRef.current = fetchList;
    }, [fetchList]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceFetchList = useCallback(debounce((term) => fetchListRef.current(term, 1), 300), []);

    const initValues = useCallback((input) => {
        const valuesArray = Array.isArray(input) ? input : (input != null && input !== '' ? [input] : []);
        if (!valuesArray.length) {
            return;
        }
        const segmentAtRequest = state.segment;

        Controller.searchDataSegment({
            segment: state.segment,
            params: {
                ...extraParams,
                ...query.parseToSend({ids: valuesArray}),
                count: valuesArray.length
            }
        }, (data) => {
            // The lookup is only used to resolve titles: some endpoints (e.g. utm.search) treat `ids`
            // as a fuzzy search, so the response may miss requested values or contain other ones.
            const requested = normalizeMultiValues(valuesArray);
            const resolved = requested.map((value) => (
                (data.list || []).find((item) => String(item.value) === String(value))
                || { value, title: String(value) }
            ));

            setState(prevState => {
                if (prevState.segment !== segmentAtRequest) {
                    return prevState;
                }
                return {
                    ...prevState,
                    values: multiselect ? resolved : resolved[0]
                };
            });
        });
    }, [state.segment, extraParams, multiselect]);

    const getList = useCallback(() => {
        let list = [...state.list];

        if (state.page < state.total) {
            list.push({
                value: 'more',
                title: reportPageStatic.more
            });
        }

        return list;
    }, [state.list, state.page, state.total, reportPageStatic.more]);

    const listItem = useCallback((data) => {
        return (
            data.item.value === 'more'
                ? (
                    <a
                        className={`rw-list-option__more ${state.loading ? 'disabled' : ''}`}
                        href="#!"
                        onClick={state.loading ? null : (event) => { event.preventDefault(); onMoreRef.current(); }}
                    >
                        {data.item.title}
                        <span className="rw-list-option__loading"><Loading inherit={true} /></span>
                    </a>
                )
                : <span>{data.item.title}</span>
        );
    }, [state.loading]);

    const onMore = useCallback(() => {
        fetchList(state.serverTerm, state.page + 1);

        // for closed-end clicks outside the element
        if (elemRef.current) {
            const input = elemRef.current.querySelector('input.rw-input-reset');
            if (input) {
                input.focus();
            }
        }
    }, [fetchList, state.serverTerm, state.page]);
    onMoreRef.current = onMore;

    const onChange = useCallback((value) => {
        setState(prevState => ({
            ...prevState,
            values: value
        }));
        if (typeof onAction === 'function') {
            onAction(value);
        }
    }, [onAction]);

    const onSearch = useCallback((searchTerm) => {
        const term = searchTerm || '';

        setState(prevState => ({
            ...prevState,
            searchTerm: term
        }));

        if (isServerTerm(term)) {
            debounceFetchList(term);
        } else if (state.serverTerm) {
            // the list is filtered by a longer term: load the full list again, filter it here
            debounceFetchList('');
        }
    }, [isServerTerm, debounceFetchList, state.serverTerm]);

    const onToggle = useCallback((open) => {
        if (!state.open && open && !state.list.length && !state.loading) {
            fetchList(state.searchTerm, 1);
        }

        setState(prevState => ({
            ...prevState,
            open
        }));
    }, [state.open, state.list.length, state.loading, state.searchTerm, fetchList]);

    // case-insensitive "contains" on the title (or the exact id); the list loaded by the server for this term
    // is shown as is (the server also matches e.g. descriptions)
    const onFilter = useCallback((item, searchTerm) => {
        const term = (searchTerm || '').trim();
        if (!term || !item || item.value === 'more') {
            return true;
        }
        if (state.serverTerm && state.serverTerm === term) {
            return true;
        }
        return String(item.title || '').toLowerCase().indexOf(term.toLowerCase()) !== -1
            || String(item.value) === term;
    }, [state.serverTerm]);

    const handleAction = useCallback(() => {
        onAction(state.values);
    }, [onAction, state.values]);

    useEffect(() => {
        initValues(values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const isChangeSegment = state.segment !== segment;

        if (isChangeSegment) {
            setState(prevState => ({
                ...prevState,
                segment: segment,
                values: values,
                list: [],
                page: 1,
                total: null,
                searchTerm: '',
                serverTerm: ''
            }));
        }
    }, [segment, values]);

    useEffect(() => {
        if (state.segment !== segment) {
            initValues();
        }
    }, [state.segment, segment, initValues]);

    // multiselect mode collapses to one line until clicked (Jira-like), see Multiselects/CollapsibleMultiselect.js
    const Component = multiselect ? CollapsibleMultiselect : DropdownList;

    useEffect(() => {
        if (multiselect) {
            const nextValues = Array.isArray(values) ? values : [];
            const currentValues = Array.isArray(state.values) ? state.values : [];

            if (!shallowEqualArray(normalizeMultiValues(currentValues), normalizeMultiValues(nextValues))) {
                setState(prevState => ({
                    ...prevState,
                    values: nextValues
                }));
            }
        } else {
            const nextValue = values || null;

            if (normalizeSingleValue(state.values) !== normalizeSingleValue(nextValue)) {
                setState(prevState => ({
                    ...prevState,
                    values: nextValue
                }));
            }
        }
    }, [values, multiselect]);

    return (
        <div className={`custom-multiselect ${state.loading ? 'custom-multiselect_loading' : ''}`} ref={elemRef}>
            <Component
                className={multiselect ? '' : 'rw-dropdownlist-select'}
                showPlaceholderWithValues
                placeholder={reportPageStatic.enterValue}
                minLength={minLength}
                dataKey="value"
                textField="title"
                data={getList()}
                open={state.open}
                value={state.values}
                disabled={['more']}
                filter={onFilter}
                itemComponent={listItem}
                onChange={onChange}
                onToggle={onToggle}
                onSearch={onSearch}
                searchTerm={state.searchTerm}
                messages={messagesStatic}
            />
            {state.loading && !state.list.length
                ? <div className="custom-multiselect__loading"><Loading inherit={true} /></div>
                : null
            }
        </div>
    );
};

DynamicSelectSegment.propTypes = {
    segment: PropTypes.string,
    values: PropTypes.array,
    count: PropTypes.number,
    multiselect: PropTypes.bool,
    extraParams: PropTypes.object,
    onAction: PropTypes.func,
    t: PropTypes.object
};

export default withTranslation(DynamicSelectSegment);
